import React from 'react';
import { Music, Star, AlertCircle, TrendingUp, Award, BookOpen } from 'lucide-react';

export default function RecommendationsDisplay({ recommendations, reasoning, confidenceScore, roleDetails, loading, error }) {
  if (loading) {
    return (
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-400 border-t-transparent mb-4"></div>
          <p className="text-white/70">Analyzing role and generating personalized recommendations...</p>
          <p className="text-white/50 text-sm mt-2">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <div className="bg-red-500/10 border border-red-500 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-200 font-medium">Failed to Generate Recommendations</p>
              <p className="text-red-200/80 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!recommendations || recommendations?.length === 0) {
    return (
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <div className="text-center py-12">
          <Music className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <p className="text-white/70 text-lg">No recommendations yet</p>
          <p className="text-white/50 text-sm mt-2">Select a role and generate recommendations to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Strategy */}
      {reasoning && (
        <div className="bg-gradient-to-r from-purple-600/30 to-indigo-600/30 backdrop-blur-md rounded-2xl p-6 border border-purple-400/30">
          <div className="flex items-start gap-3 mb-3">
            <TrendingUp className="w-6 h-6 text-purple-300 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-white font-bold text-lg mb-2">Audition Strategy</h3>
              <p className="text-white/90 text-sm leading-relaxed">{reasoning}</p>
            </div>
          </div>
          
          {/* Confidence Score */}
          {confidenceScore && (
            <div className="mt-4 flex items-center gap-2">
              <Star className="w-4 h-4 text-yellow-400 fill-current" />
              <span className="text-white/70 text-sm">
                Confidence Score: {Math.round(confidenceScore * 100)}%
              </span>
            </div>
          )}
        </div>
      )}

      {/* Role Context */}
      {roleDetails && (
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
          <div className="flex gap-4">
            <img
              src={roleDetails?.musical?.posterUrl || '/assets/images/no_image.png'}
              alt={roleDetails?.musical?.title}
              className="w-20 h-30 object-cover rounded-lg"
            />
            <div className="flex-1">
              <p className="text-white/70 text-sm mb-1">Recommendations for</p>
              <h3 className="text-white font-bold text-lg">{roleDetails?.characterName}</h3>
              <p className="text-purple-300 text-sm">{roleDetails?.musical?.title}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="px-2 py-1 bg-purple-500/30 text-purple-200 rounded text-xs">
                  {roleDetails?.voiceType?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                </span>
                {roleDetails?.vocalRange && (
                  <span className="px-2 py-1 bg-blue-500/30 text-blue-200 rounded text-xs">
                    {roleDetails?.vocalRange}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Song Recommendations */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <div className="flex items-center gap-3 mb-6">
          <Music className="w-6 h-6 text-pink-400" />
          <h3 className="text-white font-bold text-xl">Recommended Songs</h3>
          <span className="px-3 py-1 bg-pink-500/30 text-pink-200 rounded-lg text-sm font-medium">
            {recommendations?.length} song{recommendations?.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="space-y-4">
          {recommendations?.map((song, index) => (
            <div
              key={index}
              className="bg-gradient-to-r from-white/5 to-white/10 rounded-xl p-5 border border-white/10 hover:border-purple-400/50 transition-all"
            >
              {/* Song Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white/50 text-sm font-medium">#{index + 1}</span>
                    <h4 className="text-white font-bold text-lg">{song?.songTitle}</h4>
                  </div>
                  <p className="text-purple-300 text-sm">{song?.musicalFrom}</p>
                </div>
                
                {/* Difficulty Badge */}
                {song?.difficultLevel && (
                  <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                    song?.difficultLevel === 'Beginner' ? 'bg-green-500/30 text-green-200' :
                    song?.difficultLevel === 'Intermediate'? 'bg-yellow-500/30 text-yellow-200' : 'bg-red-500/30 text-red-200'
                  }`}>
                    {song?.difficultLevel}
                  </span>
                )}
              </div>

              {/* Reasoning */}
              {song?.reasoning && (
                <div className="mb-3">
                  <p className="text-white/80 text-sm leading-relaxed">{song?.reasoning}</p>
                </div>
              )}

              {/* Vocal Highlights */}
              {song?.vocalHighlights && (
                <div className="flex items-start gap-2 mb-2">
                  <Award className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white/70 text-xs font-medium mb-1">Showcases:</p>
                    <p className="text-white/90 text-sm">{song?.vocalHighlights}</p>
                  </div>
                </div>
              )}

              {/* Caution Notes */}
              {song?.cautionNotes && (
                <div className="flex items-start gap-2 mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-yellow-200 text-xs font-medium mb-1">Note:</p>
                    <p className="text-yellow-100 text-sm">{song?.cautionNotes}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => window.print()}
            className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            <BookOpen className="w-5 h-5" />
            Print Recommendations
          </button>
          <button
            onClick={() => {
              const text = recommendations?.map((s, i) => 
                `${i + 1}. ${s?.songTitle} from ${s?.musicalFrom}\n   ${s?.reasoning}`
              )?.join('\n\n');
              navigator?.clipboard?.writeText(`Audition Song Recommendations\n\n${reasoning}\n\n${text}`);
              alert('Recommendations copied to clipboard!');
            }}
            className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Star className="w-5 h-5" />
            Copy to Clipboard
          </button>
        </div>
      </div>
    </div>
  );
}